import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Mail, Loader2, ArrowLeft, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function EmailPreferences() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [prefs, setPrefs] = useState({ email: '', marketing: true, unsubscribe_url: '' });

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(`${API}/api/account/email-preferences`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setPrefs(data);
      } catch (e) {
        toast.error("Impossible de charger vos préférences");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const toggleMarketing = async (value) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/api/account/email-preferences`,
        { marketing: value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPrefs((p) => ({ ...p, marketing: value }));
      toast.success(value ? "Vous recevrez à nouveau nos emails" : "Emails marketing désactivés");
    } catch (e) {
      toast.error("Échec de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="email-prefs-loading">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-950 text-white px-4 py-8" data-testid="email-preferences-page">
      <div className="max-w-2xl mx-auto">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-neutral-400 hover:text-emerald-400 mb-6" data-testid="back-link">
          <ArrowLeft className="w-4 h-4" /> Retour au dashboard
        </Link>

        <div className="flex items-start gap-4 mb-8">
          <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
            <Mail className="w-6 h-6 text-emerald-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Préférences email</h1>
            <p className="text-neutral-400 text-sm mt-1">
              Choisissez le type d&apos;emails que vous souhaitez recevoir de la part de <span className="text-emerald-400 font-semibold">TRADEX AI</span>.
            </p>
          </div>
        </div>

        <div className="bg-neutral-900/60 border border-neutral-800 rounded-xl p-5 mb-4" data-testid="account-email-row">
          <div className="text-xs uppercase tracking-widest text-neutral-500 mb-1">Compte</div>
          <div className="font-mono text-sm">{prefs.email}</div>
        </div>

        {/* Marketing */}
        <div className="bg-neutral-900/60 border border-neutral-800 rounded-xl p-5 mb-4 flex items-start justify-between gap-4" data-testid="marketing-toggle-row">
          <div>
            <div className="font-semibold mb-1">Emails marketing et conseils trading</div>
            <div className="text-sm text-neutral-400 leading-relaxed">
              Nouveautés produit, guides ICT, promotions, packs, et conseils Money Management.
              Environ 1 à 2 emails par semaine.
            </div>
          </div>
          <Switch
            checked={prefs.marketing}
            onCheckedChange={toggleMarketing}
            disabled={saving}
            data-testid="marketing-switch"
          />
        </div>

        {/* Transactional (always on, informational) */}
        <div className="bg-neutral-900/40 border border-neutral-800/60 rounded-xl p-5 mb-6 flex items-start justify-between gap-4" data-testid="transactional-row">
          <div>
            <div className="font-semibold mb-1 flex items-center gap-2">
              Emails transactionnels
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-neutral-800 text-neutral-400 uppercase tracking-widest">Toujours actifs</span>
            </div>
            <div className="text-sm text-neutral-400 leading-relaxed">
              Confirmations d&apos;abonnement, factures Stripe, limite d&apos;analyses atteinte, réinitialisation de mot de passe.
              Nécessaires au fonctionnement du service.
            </div>
          </div>
          <Shield className="w-5 h-5 text-neutral-500 mt-1" />
        </div>

        <div className="text-xs text-neutral-500 leading-relaxed">
          En cas de besoin, vous pouvez également vous désinscrire en un clic depuis le lien présent en bas de chaque email marketing.
        </div>
      </div>
    </div>
  );
}
